import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { arrowRotateCwIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { IconsModule } from '@progress/kendo-angular-icons';
import { SkeletonModule } from '@progress/kendo-angular-indicators';
import { CoverageData, WidgetData } from '../../../../../../../core/services/analytics.service';
import { MetricCardComponent } from '../../../../../../../core/components/metric-card/metric-card.component';
import { environment } from '../../../../../../../../environments/environment';
import { EdrGridDialogComponent, EdrGridColumn } from '../edr-grid-dialog/edr-grid-dialog.component';

@Component({
  selector: 'app-hud',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonModule, IconsModule, SkeletonModule, MetricCardComponent, EdrGridDialogComponent],
  templateUrl: './hud.component.html',
  styleUrl: './hud.component.scss',
})
export class HudComponent {
  public threats = input<WidgetData>({ value: 0, colors: [] });
  public threatReboot = input<WidgetData>({ value: 0, colors: [] });
  public outdatedAgents = input<WidgetData>({ value: 0, colors: [] });
  public coverage = input<CoverageData>({ value: [] });
  public organizationId = input<number | null>(null);
  /** [start, end] date range from the EDR tab period selector, for CSV export. */
  public dateRange = input<string[] | null>(null);

  public threatsLoading = input<boolean>(true);
  public threatRebootLoading = input<boolean>(true);
  public outdatedAgentsLoading = input<boolean>(true);
  public coverageLoading = input<boolean>(true);

  public refresh = output<void>();
  public arrowRotateCwIcon: SVGIcon = arrowRotateCwIcon;

  public showOpenThreatsDialog = signal<boolean>(false);
  public showThreatRebootDialog = signal<boolean>(false);
  public showOutdatedAgentsDialog = signal<boolean>(false);
  public showCoverageDialog = signal<boolean>(false);

  private apiBase = `${environment.apiUrl}/v1/analytics/administrative`;

  private detailUrl = (entity: string) =>
    computed(() => {
      const id = this.organizationId();
      return id != null ? `${this.apiBase}/${entity}(${id})` : '';
    });

  public openThreatsUrl = this.detailUrl('edropenthreatsdetails');
  public threatRebootUrl = this.detailUrl('edrthreatrebootdetails');
  public outdatedAgentsUrl = this.detailUrl('edroutdatedagentsdetails');
  public coverageUrl = this.detailUrl('edrcoveragedetails');

  public openThreatsColumns: EdrGridColumn[] = [
    { field: 'timestamp', title: 'Identified At', filterable: false, type: 'date' },
    { field: 'created_at', title: 'Reported At', filterable: false, type: 'date' },
    { field: 'agent_id', title: 'Agent ID' },
    { field: 'threat_name', title: 'Threat Name' },
    { field: 'endpoint_name', title: 'Device | Endpoint Name' },
    { field: 'mitigation_status', title: 'Status', filterable: false },
  ];

  public threatRebootColumns: EdrGridColumn[] = [
    { field: 'timestamp', title: 'Last Observed At', type: 'date', filterable: false },
    { field: 'agent_id', title: 'Agent ID' },
    { field: 'endpoint_name', title: 'Device | Endpoint Name' },
    { field: 'device_type', title: 'Device | Endpoint Type' },
    { field: 'site_name', title: 'Tenant Name' },
    { field: 'group_name', title: 'Group Name' },
    { field: 'threat_reboot_required', title: 'Reboot Required', filterable: false, type: 'boolean' },
  ];

  public outdatedAgentsColumns: EdrGridColumn[] = [
    { field: 'timestamp', title: 'Last Observed At', type: 'date', filterable: false },
    { field: 'agent_id', title: 'Agent ID' },
    { field: 'endpoint_name', title: 'Device | Endpoint Name' },
    { field: 'device_type', title: 'Device | Endpoint Type' },
    { field: 'agent_version', title: 'Agent Version' },
    { field: 'site_name', title: 'Tenant Name' },
    { field: 'group_name', title: 'Group Name' },
  ];

  public coverageColumns: EdrGridColumn[] = [
    { field: 'timestamp', title: 'Last Observed At', type: 'date', filterable: false },
    { field: 'agent_id', title: 'Agent ID' },
    { field: 'agent_version', title: 'Agent Version' },
    { field: 'endpoint_name', title: 'Device | Endpoint Name' },
    { field: 'device_type', title: 'Device | Endpoint Type' },
    { field: 'network_status', title: 'Network Status' },
    { field: 'vendor', title: 'Vendor' },
    { field: 'site_name', title: 'Tenant Name' },
    { field: 'group_name', title: 'Group Name' },
  ];

  private coverageValue(name: string): number | null {
    return this.coverage().value?.find((item) => item.name === name)?.value ?? null;
  }

  public coveragePercentage = computed(() => this.coverageValue('Coverage'));
  public coverageObserved = computed(() => this.coverageValue('Actual'));
  public coveragePurchased = computed(() => this.coverageValue('Expected'));

  public coveragePercentageDisplay = computed(() => {
    const value = this.coveragePercentage();
    if (value === null) return 'N/A';
    return `${value}%`;
  });

  public coveragePurchasedDisplay = computed(() => {
    const value = this.coveragePurchased();
    if (value === null) return '—';
    return value === -1 ? 'Unlimited' : value;
  });

  refreshHud(): void {
    this.refresh.emit();
  }
}
